import React from 'react';
import ReactDOM from 'react-dom';
import { Search, Check, X } from 'lucide-react';
import { normalizeRut } from '../../utils/rutValidator';
import { normalizeText } from '../../utils/textNormalization';
import { useDropdownPortal } from '../../hooks/useDropdownPortal';
import {
  FORM_BORDER,
  FORM_BORDER_HOVER,
  FORM_BORDER_ERROR,
  FOCUS_RING,
  FOCUS_RING_ERROR,
  FORM_RADIUS,
  FORM_SHADOW,
  DROPDOWN_ANIMATION,
  DROPDOWN_BG,
  ITEM_HOVER,
  LABEL_TEXT,
  ERROR_TEXT,
} from './tokens';

export interface ComboboxOption {
  value: string;
  label: string;
  subtitle?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  allowClear?: boolean;
  minSearchLength?: number;
}

export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value,
  onChange,
  onSearchChange,
  placeholder = 'Buscar...',
  label,
  required = false,
  error,
  disabled = false,
  emptyMessage = 'No se encontraron resultados',
  className = '',
  allowClear: _allowClear = false,
  minSearchLength = 0
}) => {
  const {
    isMounted,
    dropdownDataState,
    searchTerm,
    setSearchTerm,
    dropdownPos,
    containerRef,
    triggerRef,
    dropdownRef,
    handleOpen,
    handleAnimationEnd,
    close,
  } = useDropdownPortal<HTMLButtonElement>(disabled);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = options.filter((option) => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const normalizedSearchTerm = normalizeText(searchTerm);

    const matchesLabel = option.label.toLowerCase().includes(lowerSearchTerm);
    const matchesNormalizedLabel = normalizeText(option.label).includes(normalizedSearchTerm);

    const matchesSubtitle = option.subtitle?.toLowerCase().includes(lowerSearchTerm);
    const matchesNormalizedSubtitle = option.subtitle ? normalizeText(option.subtitle).includes(normalizedSearchTerm) : false;

    const normalizedRut = normalizeRut(searchTerm);
    const hasNormalizedContent = normalizedRut.length > 0;
    const normalizedSubtitleRut = option.subtitle ? normalizeRut(option.subtitle) : '';
    const matchesNormalizedRut = hasNormalizedContent && normalizedSubtitleRut.includes(normalizedRut);

    return matchesLabel || matchesNormalizedLabel || matchesSubtitle || matchesNormalizedSubtitle || matchesNormalizedRut;
  });

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    close();
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
  };

  const renderDropdownContent = () => {
    if (minSearchLength > 0 && searchTerm.length < minSearchLength) {
      return (
        <div className="px-4 py-3 text-sm text-slate-500 text-center">
          Escriba al menos {minSearchLength} caracteres para buscar
        </div>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <div className="px-4 py-3 text-sm text-slate-500 text-center">
          {emptyMessage}
        </div>
      );
    }

    return filteredOptions.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => handleSelect(option.value)}
        className={`w-full px-4 py-2.5 text-left ${ITEM_HOVER} transition-colors flex items-center justify-between ${
          option.value === value ? 'bg-blue-50' : ''
        }`}
      >
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-800">
            {option.label}
          </div>
          {option.subtitle && (
            <div className="text-xs text-slate-500 mt-0.5">
              {option.subtitle}
            </div>
          )}
        </div>
        {option.value === value && (
          <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
        )}
      </button>
    ));
  };

  let triggerTextColor = 'text-slate-400';
  if (selectedOption) triggerTextColor = 'text-slate-800';
  else if (disabled) triggerTextColor = 'text-slate-700';

  const triggerClasses = [
    'w-full px-4 py-3 text-left transition flex items-center justify-between',
    FORM_RADIUS,
    error ? FORM_BORDER_ERROR : `${FORM_BORDER} ${FORM_BORDER_HOVER}`,
    error ? FOCUS_RING_ERROR : FOCUS_RING,
    disabled ? 'bg-slate-50 cursor-not-allowed' : 'bg-white active:scale-[0.98]',
  ].join(' ');

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className={`block ${LABEL_TEXT} mb-1.5`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className={triggerClasses}
        >
          <span className={triggerTextColor}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="flex items-center gap-2">
            {selectedOption && !disabled && value !== '' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
            <Search className="w-5 h-5 text-slate-400" />
          </div>
        </button>

        {isMounted && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            data-state={dropdownDataState}
            data-dropdown-portal="true"
            onAnimationEnd={handleAnimationEnd}
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
            className={`fixed z-[200] ${DROPDOWN_BG} ${FORM_RADIUS} ${FORM_SHADOW} ${DROPDOWN_ANIMATION} max-h-60 overflow-hidden dropdown-portal-protected`}
          >
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  placeholder="Buscar..."
                  className={`w-full pl-9 pr-3 py-2 text-sm ${FORM_BORDER} ${FORM_RADIUS} ${FOCUS_RING}`}
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto">
              {renderDropdownContent()}
            </div>
          </div>,
          document.body
        )}
      </div>

      {error && <p className={ERROR_TEXT}>{error}</p>}
    </div>
  );
};
